import { Inject, Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { AsyncSubject, Observable, firstValueFrom } from 'rxjs';
import { first, map, tap } from 'rxjs/operators';
import { FeatureFlag, IConfiguration } from '../interfaces/configuration';
import { serverUrlToken } from '../injection-tokens';

@Injectable({
  providedIn: 'root',
})
export class ConfigurationService {
  // Use HttpBackend directly to bypass all HTTP interceptors (including
  // MsalInterceptor). This prevents a circular dependency where loading the
  // bootstrap configuration triggers MSAL interceptor instantiation, which
  // requires the very config we are trying to load.
  private readonly httpClient: HttpClient;

  public configuration = new AsyncSubject<IConfiguration>();
  private currentConfiguration?: IConfiguration;
  private loadingPromise?: Promise<IConfiguration>;

  constructor(
    httpBackend: HttpBackend,
    @Inject(serverUrlToken) private readonly serverUrl: string,
  ) {
    this.httpClient = new HttpClient(httpBackend);
  }

  public LoadConfiguration(): Promise<IConfiguration> {
    if (this.currentConfiguration) {
      return Promise.resolve(this.currentConfiguration);
    }

    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    this.loadingPromise = this.loadConfigurationWithRetry();
    return this.loadingPromise;
  }

  public getConfiguration(): IConfiguration {
    if (!this.currentConfiguration) {
      throw new Error(
        'Configuration not loaded. Ensure the APP_INITIALIZER completed before auth initialization.',
      );
    }
    return this.currentConfiguration;
  }

  public hasFeatureFlag(flag: FeatureFlag): Observable<boolean> {
    return this.configuration.pipe(
      map((c: IConfiguration) => {
        const hasFeatureFlag = c.featureFlags.hasOwnProperty(flag) && c.featureFlags[flag];
        return hasFeatureFlag;
      }),
    );
  }

  private loadConfigurationWithRetry(): Promise<IConfiguration> {
    return firstValueFrom(
      this.httpClient
        .get<IConfiguration>(`${this.serverUrl}/api/configuration`)
        .pipe(
          first(),
          tap((configuration) => {
            this.currentConfiguration = configuration;
            this.configuration.next(configuration);
            this.configuration.complete();
          }),
        ),
    ).catch(
      () =>
        new Promise<IConfiguration>((resolve) => {
          setTimeout(() => {
            this.loadingPromise = undefined;
            resolve(this.LoadConfiguration());
          }, 5000);
        }),
    );
  }
}
